import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Upload, Loader2 } from 'lucide-react';
import { Switch } from '../ui/switch';
import { API, getAuthHeaders } from '../../utils/adminAuth';

export default function ProductModal({ product, onClose, onSave }) {
  const isEdit = !!product;
  // Variants get a stable synthetic `_key` so React doesn't reuse the wrong
  // DOM node when the user reorders or deletes one — using array index as
  // the key causes silent input-state bleed across rows.
  const withKeys = (arr) => (arr || []).map(v => ({ ...v, _key: v._key || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36)) }));
  const [form, setForm] = useState({
    name: product?.name || '',
    description: product?.description || '',
    gender: product?.gender || 'Men',
    category: product?.category || 'Perfume',
    base_price: product?.base_price || 299,
    stock: product?.stock || 100,
    image_url: product?.image_url || '',
    enable_variants: product?.enable_variants || false,
    variants: withKeys(product?.variants),
    is_signature: product?.is_signature || false,
    signature_tagline: product?.signature_tagline || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [customCategoryMode, setCustomCategoryMode] = useState(false);
  const [categoryOpts, setCategoryOpts] = useState(['Perfume', 'Helmet Spray', 'Home & Linen', 'Car Spray']);
  useEffect(() => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('auth_token')}` };
    Promise.all([
      axios.get(`${API}/admin/business-config`, { headers }).catch(() => ({ data: {} })),
      axios.get(`${API}/categories`).catch(() => ({ data: [] })),
    ]).then(([cfg, cats]) => {
      const fromCfg = (cfg.data?.category_pricing || []).map(c => c.category).filter(Boolean);
      const fromProducts = cats.data || [];
      const merged = Array.from(new Set([...fromCfg, ...fromProducts])).filter(Boolean);
      if (merged.length > 0) setCategoryOpts(merged);
    });
  }, []);

  const handleCategoryChange = (val) => {
    if (val === '__add_new__') {
      setCustomCategoryMode(true);
      setForm({ ...form, category: '' });
    } else {
      setCustomCategoryMode(false);
      setForm({ ...form, category: val });
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/product-image`, formData, { headers: getAuthHeaders() });
      const imageUrl = `${API}/files/${res.data.storage_path}`;
      setForm(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    setUploadingImage(false);
  };

  const addVariant = () => {
    setForm(prev => ({
      ...prev,
      variants: [...prev.variants, {
        _key: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36),
        size: '50ml', type: 'EDP', price: prev.base_price, stock: 100,
      }],
    }));
  };

  const removeVariant = (idx) => {
    setForm(prev => ({ ...prev, variants: prev.variants.filter((_, i) => i !== idx) }));
  };

  const updateVariant = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => i === idx ? { ...v, [field]: value } : v),
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        base_price: parseFloat(form.base_price) || 0,
        stock: parseInt(form.stock) || 0,
        category: 'Perfume',
        is_signature: form.is_signature,
        signature_tagline: form.is_signature ? form.signature_tagline : '',
        variants: form.enable_variants ? form.variants.map(v => {
          // Strip the synthetic _key before persisting — it's UI-only.
          const { _key, ...rest } = v;
          return { ...rest, price: parseFloat(v.price) || 0, stock: parseInt(v.stock) || 0 };
        }) : [],
      };
      if (isEdit) {
        await axios.put(`${API}/admin/products/${product.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/admin/products`, payload, { headers: getAuthHeaders() });
        toast.success('Product created');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="product-modal">
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-lg text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {isEdit ? 'Edit Product' : 'Add Product'}
          </h2>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="close-modal-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-4">
          <input type="text" placeholder="Product Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
            data-testid="product-name-input" />

          <textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            className="w-full bg-transparent border border-white/20 text-white py-2 px-3 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30 min-h-[60px] resize-none"
            data-testid="product-description-input" />

          <div className="grid grid-cols-2 gap-4">
            <select value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })}
              className="bg-[#141414] border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
              data-testid="product-gender-select">
              <option value="Men">Men</option>
              <option value="Women">Women</option>
              <option value="Unisex">Unisex</option>
            </select>

            {customCategoryMode ? (
              <div className="flex gap-2">
                <input
                  type="text"
                  autoFocus
                  value={form.category}
                  onChange={e => setForm({ ...form, category: e.target.value })}
                  placeholder="e.g. Body Mist, Diffuser, Reed…"
                  className="flex-1 bg-[#141414] border border-[#D4AF37]/50 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                  data-testid="product-category-custom-input"
                />
                <button
                  type="button"
                  onClick={() => { setCustomCategoryMode(false); setForm({ ...form, category: categoryOpts[0] || 'Perfume' }); }}
                  className="text-xs uppercase tracking-[0.18em] text-white/50 hover:text-white px-2 font-sans whitespace-nowrap"
                  data-testid="product-category-cancel-custom"
                >
                  ← Pick existing
                </button>
              </div>
            ) : (
              <select value={form.category} onChange={e => handleCategoryChange(e.target.value)}
                className="bg-[#141414] border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                data-testid="product-category-select">
                {categoryOpts.map(c => <option key={c} value={c}>{c}</option>)}
                {!categoryOpts.includes(form.category) && form.category && (
                  <option value={form.category}>{form.category}</option>
                )}
                <option disabled style={{color: '#444'}}>──────────</option>
                <option value="__add_new__" style={{color: '#D4AF37'}}>+ Add new category…</option>
              </select>
            )}
          </div>
          {customCategoryMode && (
            <p className="text-[11px] text-[#D4AF37]/70 font-sans -mt-2 mb-2" data-testid="custom-category-hint">
              💡 After saving, head to <strong>Pricing &amp; Program</strong> and add a tier card for &ldquo;{form.category || 'this category'}&rdquo; so ambassadors and distributors get the right price.
            </p>
          )}

          <div className="grid grid-cols-2 gap-4">
            <input type="number" placeholder="Base Price" value={form.base_price} onChange={e => setForm({ ...form, base_price: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
              data-testid="product-price-input" />

            <input type="number" placeholder="Stock" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
              data-testid="product-stock-input" />
          </div>

          {/* Image Upload */}
          <div>
            <p className="text-xs uppercase tracking-wider text-white/40 mb-2 font-sans">Product Image</p>
            <div
              className={`border-2 border-dashed rounded-none p-4 text-center cursor-pointer transition-colors ${
                uploadingImage ? 'border-[#D4AF37]/50 bg-[#D4AF37]/5' : form.image_url ? 'border-[#D4AF37]/30' : 'border-white/20 hover:border-white/40'
              }`}
              onClick={() => !uploadingImage && document.getElementById('product-image-upload')?.click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#D4AF37'; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = ''; }}
              onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = ''; handleImageUpload(e.dataTransfer.files[0]); }}
              data-testid="product-image-dropzone"
            >
              <input
                id="product-image-upload"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={e => handleImageUpload(e.target.files[0])}
                data-testid="product-image-file-input"
              />
              {uploadingImage ? (
                <div className="py-4">
                  <Loader2 className="w-6 h-6 text-[#D4AF37] mx-auto animate-spin" />
                  <p className="text-white/40 text-xs mt-2 font-sans">Uploading...</p>
                </div>
              ) : form.image_url ? (
                <div>
                  <img src={form.image_url} alt="Preview" className="w-20 h-20 object-cover mx-auto mb-2 border border-white/10" />
                  <p className="text-white/40 text-xs font-sans">Drag & drop or click to replace</p>
                </div>
              ) : (
                <div className="py-4">
                  <Upload className="w-6 h-6 text-white/30 mx-auto mb-2" />
                  <p className="text-white/50 text-sm font-sans">Drag & drop image here</p>
                  <p className="text-white/30 text-xs mt-1 font-sans">or click to browse &middot; JPG, PNG, WebP &middot; Max 5MB</p>
                </div>
              )}
            </div>
            {/* Fallback URL input */}
            <input type="text" placeholder="Or paste image URL" value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white py-2 text-xs focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20 mt-2"
              data-testid="product-image-input" />
          </div>

          {/* Signature Toggle */}
          <div className="flex items-center justify-between py-2 border-t border-white/10 mt-2">
            <div>
              <p className="text-white text-sm font-sans">JS Signature Scent</p>
              <p className="text-white/30 text-xs font-sans">Mark as JS Original creation</p>
            </div>
            <Switch checked={form.is_signature} onCheckedChange={v => setForm({ ...form, is_signature: v })} data-testid="signature-toggle" />
          </div>

          {form.is_signature && (
            <input type="text" placeholder="Signature tagline (e.g. Bold. Sweet. Unforgettable.)" value={form.signature_tagline} onChange={e => setForm({ ...form, signature_tagline: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
              data-testid="signature-tagline-input" />
          )}

          {/* Variant Toggle */}
          <div className="flex items-center justify-between py-2 border-t border-white/10 mt-2">
            <div>
              <p className="text-white text-sm font-sans">Enable Variants</p>
              <p className="text-white/30 text-xs font-sans">Add size and type options</p>
            </div>
            <Switch checked={form.enable_variants} onCheckedChange={v => setForm({ ...form, enable_variants: v })} data-testid="variant-toggle" />
          </div>

          {/* Variants */}
          {form.enable_variants && (
            <div className="space-y-3" data-testid="variants-section">
              {form.variants.map((v, idx) => (
                <div key={v._key} className="bg-[#141414] border border-white/5 p-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-white/40 text-xs font-sans">Variant {idx + 1}</span>
                    <button onClick={() => removeVariant(idx)} className="text-white/30 hover:text-red-400" data-testid={`remove-variant-${idx}`}><X className="w-3 h-3" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <select value={v.size} onChange={e => updateVariant(idx, 'size', e.target.value)}
                      className="bg-[#0A0A0A] border border-white/20 text-white text-xs px-2 py-1.5" data-testid={`variant-size-${idx}`}>
                      {['10ml', '30ml', '50ml', '100ml'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={v.type} onChange={e => updateVariant(idx, 'type', e.target.value)}
                      className="bg-[#0A0A0A] border border-white/20 text-white text-xs px-2 py-1.5" data-testid={`variant-type-${idx}`}>
                      {['EDP'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input type="number" placeholder="Price" value={v.price} onChange={e => updateVariant(idx, 'price', e.target.value)}
                      className="bg-transparent border-b border-white/20 text-white text-xs py-1 focus:border-[#D4AF37] focus:outline-none" data-testid={`variant-price-${idx}`} />
                    <input type="number" placeholder="Stock" value={v.stock} onChange={e => updateVariant(idx, 'stock', e.target.value)}
                      className="bg-transparent border-b border-white/20 text-white text-xs py-1 focus:border-[#D4AF37] focus:outline-none" data-testid={`variant-stock-${idx}`} />
                  </div>
                </div>
              ))}
              <button onClick={addVariant} className="w-full border border-dashed border-white/20 text-white/40 hover:text-white py-2 text-sm transition-colors" data-testid="add-variant-btn">
                + Add Variant
              </button>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-white/10">
          <button onClick={handleSave} disabled={saving}
            className="w-full bg-[#D4AF37] text-black py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50"
            data-testid="save-product-btn">
            {saving ? 'Saving...' : isEdit ? 'Update Product' : 'Create Product'}
          </button>
        </div>
      </div>
    </div>
  );
}
